// BRK-S152 — the indemnitor step, drawn as a ROSTER.
//
// Greg's screen: one block per owner ("Indemnitor (Primary Owner)" for the
// first), a SPOUSE block beneath any owner whose marital status puts a spouse on
// the signer list, and "+ Add owner" under the lot with the rule's own sentence
// beside it.
//
// ── BRK-S164: A CARD PER OWNER, AND THE NOTES ARE ON IT ─────────────────────
//
// Every owner used to be open at once, with an amber checklist above the stack
// saying things about people whose blocks were somewhere below it. Aaron's
// 2026-09-21 walk is what that costs on a real cap table: a page of identical
// open forms, and a note about Alice that you have to go and find Alice to act
// on. So a card is OPEN while it is being filled in and COLLAPSED once it has
// been saved, each card carries the seats the evaluation raised against THAT
// person (`rosterCardStatus`), and a collapsed card with an unsettled seat
// wears an amber marker rather than a sentence it has no room for.
//
// The collapse is what makes the SPOUSE line in the header load-bearing: a
// spouse is drawn inside their owner's card, so folding it hides a second
// person, and a header that did not name them would read as an owner who never
// said they were married.
//
// ── WHAT THIS COMPONENT DOES NOT DO ─────────────────────────────────────────
//
// It does not choose signers. "+ Add owner" edits the CAP TABLE (reconciliation
// decision (b)) — a person with a share — and the signer list is re-evaluated
// upstream from it. An owner added below the line is kept on the account and
// simply not listed, which is the correct outcome and not a rejection.
//
// It also draws no control of its own: every field comes from the configuration
// and goes through `BondFieldRenderer`, and the shared `AddressFieldset` is used
// for the spouse exactly as it is for the owner (no fork).
//
// ⚠️ WITH ONE EXCEPTION, AND IT IS NOT A FIELD (BRK-S156). The PRIMARY
// INDEMNITOR radio addresses no contract path: it says which person on the
// roster the personal credit check is run against, and it rides out on the
// save's `owners` declaration rather than in its `values`. That is why it is
// drawn here and not by `BondFieldRenderer` — there is nothing in the
// configuration for the renderer to have declared.
import { BondFieldRenderer } from "@/components/BondFieldRenderer";
import type { BondFilesPort } from "@/components/BondFilesField";
import type { BondPiiPort } from "@/components/BondSsnField";
import { BondMissingChecklist } from "@/components/BondMissingChecklist";
import { asAddress, fieldKind } from "@/lib/bondFields";
import { formatAddress, isCompleteAddress } from "@/lib/address";
import type {
  BondFieldValue,
  BondRenderField,
  BondValueBag,
} from "@/lib/bondFields";
import {
  PRIMARY_INDEMNITOR_LABEL,
  PRIMARY_INDEMNITOR_NOTE,
  ROSTER_SOFT_LIMIT,
  ownersIncludedCopy,
  rosterCardStatus,
  rosterCardSummaries,
  type RosterBlock,
  type RosterCardSummary,
  type RosterPerson,
} from "@/lib/bondRoster";
import type { BondIndemnity, BondIndemnityRule } from "@/lib/brokerBonds";

/** An address or a long answer takes the whole row; a scalar sits in the
 *  two-column grid from `sm:` up. The same rule the ordinary fields step
 *  applies, stated once here for the blocks. */
function fullRow(field: BondRenderField): boolean {
  const kind = fieldKind(field);
  return kind === "address" || kind === "textarea" || kind === "unsupported";
}

function BlockFields({
  fields,
  values,
  onChange,
  invalid,
  pendingReview,
  filesPort,
  piiPort,
}: {
  fields: readonly BondRenderField[];
  values: BondValueBag;
  onChange: (name: string, value: BondFieldValue | undefined) => void;
  invalid: Record<string, string>;
  pendingReview: (name: string) => boolean;
  filesPort?: BondFilesPort;
  piiPort?: BondPiiPort;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {fields.map((f) => (
        <div
          key={f.name}
          // BRK-S319 — where an error summary's link sends focus.
          data-field={f.name}
          className={fullRow(f) ? "sm:col-span-2" : undefined}
        >
          <BondFieldRenderer
            field={f}
            value={values[f.name]}
            onChange={(v) => onChange(f.name, v)}
            error={invalid[f.name]}
            pendingReview={pendingReview(f.name)}
            files={filesPort}
            pii={piiPort}
          />
        </div>
      ))}
    </div>
  );
}

/** One seat's note, rendered where the seat is ABOUT (BRK-S164). Amber rather
 *  than red: nothing here is an error the broker made, it is work the request
 *  still owes. */
function SignerNotes({ notes }: { notes: readonly string[] }) {
  if (notes.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1">
      {notes.map((note) => (
        <li
          key={note}
          className="rounded-md border border-amber-300 dark:border-amber-400/35 bg-amber-50 dark:bg-amber-400/10 px-3 py-1.5 text-sm text-amber-900 dark:text-amber-200"
        >
          {note}
        </li>
      ))}
    </ul>
  );
}

/** The marker a COLLAPSED card wears while a seat on it is unsettled. A card
 *  with no room for a sentence still has to say that there is one — the
 *  sentence itself is one click away under Edit. */
function AttentionMarker() {
  return (
    <span
      role="img"
      aria-label="Needs attention"
      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-amber-500"
    />
  );
}

export interface BondIndemnityRosterProps {
  blocks: readonly RosterBlock[];
  /** The people the blocks are drawn for — what the collapsed headers are
   *  summarised from (`rosterCardSummaries`, read off `values`). */
  roster: readonly RosterPerson[];
  values: BondValueBag;
  onChange: (name: string, value: BondFieldValue | undefined) => void;
  invalid: Record<string, string>;
  pendingReview: (name: string) => boolean;
  filesPort?: BondFilesPort;
  /** BRK-S316 — what a stored SSN's masked control needs (the session's held
   *  value and the Change state). Absent, a stored SSN renders as BRK-S105 did. */
  piiPort?: BondPiiPort;
  /**
   * The rule the footer copy quotes — the EVALUATION's, when the order carries
   * one. `null` says the progress bar above is already quoting it, and the
   * same sentence twice is one sentence nobody reads (BRK-S156/S164).
   */
  rule: BondIndemnityRule | null;
  /**
   * The order's evaluation, or null when there is none to read — a product
   * that asks for no personal indemnity, and a PMS that predates the block.
   * Every card note comes off this and nothing this page worked out.
   */
  indemnity: BondIndemnity | null;
  /**
   * WHOSE credit is pulled. One owner block carries the checked radio, and it
   * is the RESPONSE's answer wherever the order has one — see
   * `effectivePrimaryOwnerId`. Undefined only on a roster with no owners, which
   * the roster does not have.
   */
  primaryOwnerId?: string;
  onPrimaryChange: (ownerId: string) => void;
  /** Which owner cards are EXPANDED. A card is open while it is being typed
   *  into and collapsed once it has been saved (BRK-S164). */
  openOwners: ReadonlySet<string>;
  onEditOwner: (ownerId: string) => void;
  /** Save THIS card — validates its own fields (the owner's and their
   *  spouse's) and PUTs the whole roster. Collapses on success; on failure the
   *  card stays open with the reason on it (never a false success —
   *  BRK-S113/S122). */
  onSaveOwner: (ownerId: string) => void;
  /** The card whose save is in flight, if any. */
  savingOwnerId?: string | null;
  /** BRK-S317 — something on the roster must change before ANY card can be
   *  saved (two people share an SSN); the reason is inline on the field. */
  saveBlocked?: boolean;
  /** Why a card's own save was refused, keyed by owner. */
  cardErrors: Readonly<Record<string, string>>;
  /**
   * The required fields a blocked save found unanswered ON EACH CARD, keyed by
   * the card's owner (a spouse's fields answer to their owner's card). Drawn
   * inside the card beside its Save, never in a step-level panel above the
   * roster that the broker would have to scroll up to find (BRK-S181).
   */
  cardMissing?: Readonly<Record<string, readonly BondRenderField[]>>;
  /** BRK-S319 — where a missing field is ("Indemnitor, primary owner"), and
   *  how to take the broker to it. Both feed the card's checklist links. */
  placeOf?: (field: BondRenderField) => string | null;
  onGoToField?: (field: BondRenderField) => void;
  /** Whether another owner row will fit inside the vendor's ceiling. */
  canAddOwner: boolean;
  onAddOwner: () => void;
  /** Removing an owner takes their spouse and their answers with them. Absent
   *  on the only remaining owner, because a roster with nobody on it is not a
   *  state this step has. */
  onRemoveOwner: (ownerId: string) => void;
  removableOwners: boolean;
  /** Spouse rows the broker says have their own address. */
  spouseAddressDiffers: ReadonlySet<string>;
  onSpouseAddressDiffersChange: (spouseId: string, differs: boolean) => void;
}

export function BondIndemnityRoster({
  blocks,
  roster,
  values,
  onChange,
  invalid,
  pendingReview,
  filesPort,
  piiPort,
  rule,
  indemnity,
  primaryOwnerId,
  onPrimaryChange,
  openOwners,
  onEditOwner,
  onSaveOwner,
  savingOwnerId,
  saveBlocked = false,
  cardErrors,
  cardMissing = {},
  placeOf,
  onGoToField,
  canAddOwner,
  onAddOwner,
  onRemoveOwner,
  removableOwners,
  spouseAddressDiffers,
  onSpouseAddressDiffersChange,
}: BondIndemnityRosterProps) {
  const summaries = new Map(
    rosterCardSummaries(roster, values).map((s) => [s.ownerId, s]),
  );
  const ownerBlocks = blocks.filter((block) => block.kind === "owner");
  return (
    <div className="flex flex-col gap-5">
      {ownerBlocks.map((block) => {
        const spouse = blocks.find(
          (b) => b.kind === "spouse" && b.aboutOwnerId === block.ownerId,
        );
        const open = openOwners.has(block.ownerId);
        const status = rosterCardStatus(indemnity, block.ownerId);
        const summary: RosterCardSummary = summaries.get(block.ownerId) ?? {
          ownerId: block.ownerId,
          name: "New owner",
          share: null,
          spouseName: null,
        };
        const heading = ownerHeading(block, primaryOwnerId);
        const isPrimary = block.ownerId === primaryOwnerId;
        return (
          <div
            key={`card-${block.ownerId}`}
            // The CARD: the owner's own block, the spouse fieldset drawn
            // inside it, and the card's own Save. `data-owner-id` stays on the
            // owner's block alone, so a query scoped to one person's controls
            // does not also reach their spouse's.
            data-owner-card={block.ownerId}
            className="flex flex-col gap-4 rounded-xl border border-brk-border bg-brk-panel p-4 sm:p-5"
          >
            <section
              // `data-owner-id` is what a blocked Save & Continue scrolls to —
              // the ROSTER's identity, which is the contract's own address for
              // a person. An attribute rather than a DOM id, which would have
              // to be unique across a page that may one day show two rosters.
              data-owner-id={block.ownerId}
              data-open={open ? "true" : "false"}
              aria-label={open ? heading : summary.name}
              // Focusable only PROGRAMMATICALLY (-1, never in the tab order):
              // a blocked Save & Continue moves the broker to the card it is
              // about, and scrolling alone moves the page without moving a
              // keyboard user (BRK-S164).
              tabIndex={-1}
              className="flex flex-col gap-4 outline-none"
            >
              {open ? (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-brk-text">
                      {heading}
                    </h3>
                    {removableOwners && (
                      <button
                        type="button"
                        onClick={() => onRemoveOwner(block.ownerId)}
                        aria-label={`Remove ${summary.name}`}
                        className="text-sm font-medium text-brk-danger hover:underline"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <SignerNotes notes={status.ownerNotes} />
                  {/* A spouse note with NO spouse fieldset to carry it — the
                      `missing` case, where there is nobody on the roster yet.
                      It belongs on the owner's own header, because adding them
                      is a thing the owner's marital status does. */}
                  {!spouse && <SignerNotes notes={status.spouseNotes} />}
                  {/* WHOSE credit is pulled (BRK-S156). On an OWNER block only
                      — a spouse signs the agreement and is never the subject of
                      the personal pull, so offering the choice there would be a
                      control for a decision nobody has. A radio rather than a
                      per-block checkbox because exactly one person holds it,
                      which is also the rule upstream enforces with a 422. */}
                  <label className="flex items-start gap-3 text-sm text-brk-text">
                    <input
                      type="radio"
                      // One group across the whole roster, so choosing here
                      // UNCHOOSES wherever it was.
                      name="primary-indemnitor"
                      className="mt-0.5 h-4 w-4"
                      checked={isPrimary}
                      onChange={() => onPrimaryChange(block.ownerId)}
                    />
                    <span>
                      <span className="font-medium">
                        {PRIMARY_INDEMNITOR_LABEL}
                      </span>
                      {" — "}
                      <span className="text-brk-dim">
                        {PRIMARY_INDEMNITOR_NOTE}
                      </span>
                    </span>
                  </label>
                  <BlockFields
                    fields={block.fields}
                    values={values}
                    onChange={onChange}
                    invalid={invalid}
                    pendingReview={pendingReview}
                    filesPort={filesPort}
                    piiPort={piiPort}
                  />
                </>
              ) : (
                <CollapsedHeader
                  summary={summary}
                  isPrimary={isPrimary}
                  attention={status.attention}
                  removable={removableOwners}
                  onEdit={() => onEditOwner(block.ownerId)}
                  onRemove={() => onRemoveOwner(block.ownerId)}
                />
              )}
            </section>

            {open && spouse && (
              <section
                data-spouse-of={spouse.aboutOwnerId}
                aria-label="Spouse"
                // Indented under its owner from `sm:` up and flush at 375px,
                // where an indent costs a column the form cannot spare.
                className="flex flex-col gap-4 rounded-xl border border-brk-border bg-brk-bg p-4 sm:ml-6 sm:p-5"
              >
                <h3 className="text-sm font-semibold text-brk-text">Spouse</h3>
                <p className="text-sm text-brk-dim">
                  The spouse of a listed owner signs the indemnity agreement
                  too.
                </p>
                <SignerNotes notes={status.spouseNotes} />
                <label className="flex items-start gap-3 text-sm text-brk-text">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4"
                    checked={spouseAddressDiffers.has(spouse.ownerId)}
                    onChange={(e) =>
                      onSpouseAddressDiffersChange(
                        spouse.ownerId,
                        e.target.checked,
                      )
                    }
                  />
                  <span>Spouse’s address differs from the owner’s</span>
                </label>
                {/* BRK-S321 — unticked, the owner's address IS the spouse's
                    (copied at save), so the block says which one it is
                    rather than leaving the broker to assume. */}
                {!spouseAddressDiffers.has(spouse.ownerId) && (
                  <OwnersAddressLine fields={block.fields} values={values} />
                )}
                <BlockFields
                  fields={spouse.fields}
                  values={values}
                  onChange={onChange}
                  invalid={invalid}
                  pendingReview={pendingReview}
                  filesPort={filesPort}
                  piiPort={piiPort}
                />
              </section>
            )}

            {open && (
              <div className="flex flex-col gap-2">
                {/* NO OPTIMISTIC SUCCESS (BRK-S113/S122). The card collapses
                    on the server's answer and on nothing else; a refused save
                    leaves it open with the reason on it, where the fields that
                    caused it are. */}
                {/* What this card still needs, where the Save that found it
                    was pressed (BRK-S181). The field markers above stay. */}
                <BondMissingChecklist
                  missing={cardMissing[block.ownerId] ?? []}
                  placeOf={placeOf}
                  onGo={onGoToField}
                />
                {cardErrors[block.ownerId] && (
                  <p role="alert" className="text-sm text-brk-danger">
                    {cardErrors[block.ownerId]}
                  </p>
                )}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => onSaveOwner(block.ownerId)}
                    disabled={savingOwnerId != null || saveBlocked}
                    aria-label={`Save ${summary.name}`}
                    className="rounded-md border border-brk-accent px-4 py-2 text-sm font-medium text-brk-accent transition-colors hover:bg-brk-bg disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {savingOwnerId === block.ownerId ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div className="flex flex-col gap-2">
        {canAddOwner ? (
          <button
            type="button"
            onClick={onAddOwner}
            className="self-start rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg"
          >
            + Add owner
          </button>
        ) : (
          // The vendor ceiling, said plainly and with a way forward. It is a
          // SOFT limit: nothing typed is discarded, and an insured with more
          // owners than this is a request an underwriter finishes.
          <p role="status" className="text-sm text-brk-dim">
            This request holds up to {ROSTER_SOFT_LIMIT} people, including
            spouses. If more owners have to be listed, submit what you have and
            we’ll finish the rest with you.
          </p>
        )}
        {rule && (
          <p className="text-sm text-brk-dim">{ownersIncludedCopy(rule)}</p>
        )}
      </div>
    </div>
  );
}

/** The owner's home address, as the spouse will be filed with it — said only
 *  when there is a complete one to say. */
function OwnersAddressLine({
  fields,
  values,
}: {
  fields: readonly BondRenderField[];
  values: BondValueBag;
}) {
  const field = fields.find((f) => fieldKind(f) === "address");
  if (!field) return null;
  const address = asAddress(values[field.name]);
  if (!isCompleteAddress(address)) return null;
  return (
    <p className="text-sm text-brk-dim">
      Uses the owner’s home address: {formatAddress(address)}
    </p>
  );
}

/**
 * A saved owner, folded to one row: who they are, what they hold, whether they
 * are the primary, and the spouse the fold is hiding (BRK-S164).
 *
 * Mobile-first: the identity and the actions wrap onto separate lines at
 * 375px, and the name truncates rather than pushing the row wide — a cap table
 * is full of long legal names and a header that overflows is a header nobody
 * can reach the Edit on.
 */
function CollapsedHeader({
  summary,
  isPrimary,
  attention,
  removable,
  onEdit,
  onRemove,
}: {
  summary: RosterCardSummary;
  isPrimary: boolean;
  attention: boolean;
  removable: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {attention && <AttentionMarker />}
          <span className="truncate text-sm font-semibold text-brk-text">
            {summary.name}
          </span>
          {summary.share && (
            <span className="text-sm text-brk-dim">{summary.share}</span>
          )}
          {isPrimary && (
            <span className="rounded-full bg-brk-accent/10 px-2 py-0.5 text-xs font-medium text-brk-accent">
              {PRIMARY_INDEMNITOR_LABEL}
            </span>
          )}
        </div>
        {summary.spouseName && (
          <span className="text-sm text-brk-dim">{`Spouse: ${summary.spouseName}`}</span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${summary.name}`}
          className="text-sm font-medium text-brk-accent hover:underline"
        >
          Edit
        </button>
        {removable && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${summary.name}`}
            className="text-sm font-medium text-brk-danger hover:underline"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Greg's heading — except the primary owner is now the one the roster MARKS,
 * not the one that happens to be first (BRK-S156).
 *
 * Leaving "primary owner" welded to block 1 while a radio two blocks down says
 * otherwise would put two answers to one question on the same screen; the
 * number is what the evaluation's "Owner, 55%" lands on, so every other block
 * keeps it.
 */
function ownerHeading(block: RosterBlock, primaryOwnerId?: string): string {
  return block.ownerId === primaryOwnerId
    ? "Indemnitor (primary owner)"
    : `Indemnitor (owner ${block.position})`;
}
